import React, { useContext, useEffect, useState } from "react"; // Add useState
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// alphabetized
import * as yup from "yup";
import qapi from "views_shared/app/javascript/api";
import SaveSpinnerButton from "./save_spinner_button";
import companySchema from "../../../yup_schemas/company_schema";
import { CompanyContext } from "../data/company_context_management";

function TwitterUrlForm() {
  const context = useContext(CompanyContext);

  const { company, isUpdating, setIsUpdating, setDoTopLevelFetch } = context;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(
      yup.object().shape({
        twitter_url: companySchema.fields.twitter_url,
      }),
    ),
  });

  const twitterContactInfo = company.contact_details?.find((detail) => detail.type === "twitter") || {};
  const twitterHandle = twitterContactInfo.value ? twitterContactInfo.value.replace("@", "") : null;
  const twitterUrl = twitterContactInfo.value ? `https://twitter.com/${twitterHandle}` : null;

  // Watch the twitter_url value
  const watchedTwitterUrl = watch("twitter_url");

  // State to manage whether the Save button is enabled
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  useEffect(() => {
    setValue("twitter_url", twitterUrl);
    setIsSaveEnabled(false); // Reset when the component is reloaded
  }, [company, setValue, twitterUrl]);

  useEffect(() => {
    // Enable the Save button if the watched value is different from the initial value
    setIsSaveEnabled(watchedTwitterUrl !== twitterUrl);
  }, [watchedTwitterUrl, twitterUrl]);

  const onSubmit = async (data, event) => {
    event.preventDefault();
    setIsUpdating(true);

    const { twitter_url: tUrl } = data;

    let tHandle = "";
    if (tUrl.length === 0) {
      tHandle = "";
    } else {
      const url = new URL(tUrl);
      const handle = url.pathname.split("/").pop();
      tHandle = `@${handle}`;
    }

    let response;

    const contactInfoId = twitterContactInfo.contact_info_id;
    if (contactInfoId) {
      if (tHandle.length === 0) {
        response = await qapi.contactInfos.deleteById(contactInfoId);
      } else {
        response = await qapi.contactInfos.updateById(contactInfoId, { value: tHandle });
      }
    } else {
      response = await qapi.contactInfos.createByContactOwner(company.id, "Id::PromoEntity::Company", {
        value: tHandle,
        value_type: "twitter",
      });
    }

    if (response.error) {
      const error = response.error?.body?.errors[0];
      setError("twitter_url", {
        type: "manual",
        message: error?.detail || "An error occurred",
      });
    } else {
      setDoTopLevelFetch(true);
      setIsSaveEnabled(false);
    }

    setIsUpdating(false);
  };

  return (
    // Top-aligned on purpose: centering re-centers the whole row when the validation message
    // appears, visibly shoving the field upward. Top alignment keeps label, field, and Save in
    // place and lets the message extend downward.
    <form onSubmit={(e) => e.preventDefault()} className="row g-2 align-items-start mb-2">
      <label htmlFor="twitter_url" className="col-sm-3 col-form-label">
        Twitter URL
      </label>
      <div className="col">
        <input
          type="url"
          className={`form-control ${errors?.twitter_url ? "is-invalid" : ""}`}
          defaultValue={twitterUrl}
          id="twitter_url"
          placeholder="https://www.twitter.com/example"
          pattern="https://.*"
          required
          {...register("twitter_url")}
        />
        {errors?.twitter_url && <div className="invalid-feedback">{errors?.twitter_url?.message}</div>}
      </div>
      <div className="col-auto">
        <SaveSpinnerButton
          activeText="Saving..."
          className={`${!isSaveEnabled ? "disabled" : ""}`}
          disabled={!isSaveEnabled}
          id="twitter-save"
          inactiveText="Save"
          isSaving={isUpdating}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </form>
  );
}

export default TwitterUrlForm;
